import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  ExpandedIndex,
  Flex,
  Spacer,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';

import {
  portfolioCovenants as commonPortfolioCovenants,
  financialCovenants as commonFinCovs,
} from '@finley-technologies/covenant-calculations';

import { colors } from 'packages/common-frontend/colors';
import {
  filterSearchRows,
  SearchBar,
  SearchRow,
} from 'packages/common-frontend/components/SearchAndCopyComponents';
import { CreditAgreement, WipConfig } from 'packages/creditAgreements/ui/types';

export const IntermediateFieldsQueryVariablesSidebar = ({
  covenantType,
  wipConfig,
  configPropertyKey,
}: {
  covenantType: 'portfolio' | 'financial';
  wipConfig: WipConfig;
  configPropertyKey: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [query, setQuery] = useState<string>('');
  const [
    openAccordionIndices,
    setOpenAccordionIndices,
  ] = useState<ExpandedIndex>([0]);

  const queryVariableGroups = useMemo(() => {
    const queryResultVariables: string[] = [];
    const allIntermediateFields: string[] = [];

    if (covenantType === 'portfolio') {
      const config = _.cloneDeep(
        getIn(wipConfig.formProps.values, configPropertyKey), // portfolioCovenants[idx]._config_
      ) as CreditAgreement.Config.PortfolioCovenant.Config;
      const getQueryResults = commonPortfolioCovenants.getPortfolioCovenantQueryResultVariablesAndErrors(
        { config },
      );
      getQueryResults.queryResultVariables.forEach((ipt) => {
        queryResultVariables.push(ipt);
      });

      config.intermediateFields.forEach(({ field }) => {
        allIntermediateFields.push(field);
      });
    } else if (covenantType === 'financial') {
      const config = _.cloneDeep(
        getIn(wipConfig.formProps.values, configPropertyKey), // financialCovenants[idx]._config_
      ) as CreditAgreement.Config.FinancialCovenant.Config;
      const getQueryResults = commonFinCovs.validate.getFinancialCovenantQueryResultVariablesAndErrors(
        { config },
      );
      getQueryResults.queryResultVariables.forEach((ipt) => {
        queryResultVariables.push(ipt);
      });

      config.intermediateFields.forEach(({ field }) => {
        allIntermediateFields.push(field);
      });
    }

    const filteredVariables = filterSearchRows({
      allRowValues: queryResultVariables,
      query,
    });

    const orderedGroupedQueryResultVariables = _.groupBy(
      _.orderBy(filteredVariables, 'asc'),
      (variable) => {
        const variableSections = variable.split('_');
        const root = variableSections[0];

        return root;
      },
    );

    const filteredIntermediateFields = filterSearchRows({
      allRowValues: allIntermediateFields,
      query,
    });

    return filteredIntermediateFields.length
      ? {
          ...orderedGroupedQueryResultVariables,
          intermediateValues: filteredIntermediateFields,
        }
      : orderedGroupedQueryResultVariables;
  }, [covenantType, query, wipConfig.formProps.values, configPropertyKey]);

  const onChangeQuery = (newQuery: string) => {
    setQuery(newQuery);

    if (!newQuery) {
      setOpenAccordionIndices([0]);
    } else {
      const numberOfFilteredGroups = _.size(queryVariableGroups);
      setOpenAccordionIndices([...Array(numberOfFilteredGroups).keys()]);
    }
  };

  return (
    <Box>
      {!isExpanded && (
        <Tooltip label="Show query variables">
          <Text
            cursor="pointer"
            h="40px"
            onClick={() => setIsExpanded(true)}
            alignContent="center"
            color={colors.black[70]}
            fontWeight={500}
            ml="40px"
          >
            Show
          </Text>
        </Tooltip>
      )}
      {isExpanded && (
        <Box ml="40px" w="30vh">
          <Flex justifyContent="right" mb="20px">
            <Tooltip label="Hide query variables">
              <Text
                cursor="pointer"
                h="40px"
                onClick={() => setIsExpanded(false)}
                alignContent="center"
                color={colors.black[70]}
                fontWeight={500}
              >
                Hide
              </Text>
            </Tooltip>
          </Flex>
          <Box
            borderRadius="8px"
            borderWidth="1px"
            borderColor={colors.gray[200]}
            overflow="auto"
            maxHeight="50vh"
            backgroundColor="white"
          >
            <Box position="sticky" top="0">
              <SearchBar query={query} onChangeQuery={onChangeQuery} />
            </Box>
            <Accordion
              allowMultiple
              index={openAccordionIndices}
              onChange={(openIndices) => setOpenAccordionIndices(openIndices)}
            >
              {_.map(queryVariableGroups, (queryVariableGroup, groupRoot) => {
                return (
                  <AccordionItem border="0" key={`accordion-${groupRoot}`}>
                    <AccordionButton>
                      <Text
                        fontSize="14px"
                        mr="8px"
                        fontWeight={500}
                        color={colors.black[95]}
                      >
                        {groupRoot}
                      </Text>
                      <Spacer />
                      <AccordionIcon color={colors.black[95]} />
                    </AccordionButton>
                    <AccordionPanel>
                      <VStack spacing="8px" alignItems="left">
                        {_.map(queryVariableGroup, (queryVariable) => (
                          <SearchRow
                            rowValue={queryVariable}
                            key={`accordion-${groupRoot}-${queryVariable}`}
                          />
                        ))}
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </Box>
        </Box>
      )}
    </Box>
  );
};
